import { useState } from 'react';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { useFinishGroup, useGroupFinishLatest } from '../../api/useGroupFinish';
import { formatUsd } from '../../utils/format';

export const FinishGroupButton = ({
  groupId,
  groupName
}: {
  groupId: string;
  groupName: string;
}) => {
  const { data: snapshot } = useGroupFinishLatest(groupId);
  const finishMutation = useFinishGroup(groupId);

  const isFinished = snapshot != null;

  const [open, setOpen] = useState(false);
  const [winThreshold, setWinThreshold] = useState('0.98');
  const [loseThreshold, setLoseThreshold] = useState('0.02');

  const handleSubmit = () => {
    finishMutation.mutate({
      winThreshold: parseFloat(winThreshold),
      loseThreshold: parseFloat(loseThreshold)
    });
    setOpen(false);
  };

  if (isFinished) {
    return (
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Chip
          label={`Finished ${new Date(snapshot.finishedAt).toLocaleDateString()} · Net ${formatUsd(snapshot.totals.groupNetUsd)}`}
          size="small"
          color="default"
          variant="outlined"
          sx={{ fontSize: '0.7rem' }}
        />
        <Button
          variant="text"
          color="inherit"
          size="small"
          disabled={finishMutation.isLoading}
          onClick={() => finishMutation.mutate({})}
          sx={{ fontSize: '0.65rem' }}
        >
          Re-finish
        </Button>
      </Stack>
    );
  }

  return (
    <>
      <Button
        color="warning"
        variant="outlined"
        size="small"
        sx={{ fontSize: '0.7rem' }}
        onClick={() => setOpen(true)}
      >
        Finish
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: '1rem' }}>
          Finish group — {groupName}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <Typography sx={{ fontSize: '0.85rem' }}>
              This will TURN OFF every rule in the group, stop its pods, and CANCEL every
              remaining open order for the group's markets before snapshotting.
            </Typography>
            <Typography sx={{ fontSize: '0.85rem' }}>
              The group and its pairs will be marked finished. The group cannot be
              re-started afterward.
            </Typography>
            <Typography sx={{ fontSize: '0.85rem' }}>
              This cancels resting orders but does NOT place or redeem orders.
            </Typography>
            <Stack direction="row" spacing={1}>
              <TextField
                label="Win threshold (≥)"
                type="number"
                size="small"
                value={winThreshold}
                onChange={e => setWinThreshold(e.target.value)}
                inputProps={{ step: 0.01, min: 0, max: 1 }}
              />
              <TextField
                label="Lose threshold (≤)"
                type="number"
                size="small"
                value={loseThreshold}
                onChange={e => setLoseThreshold(e.target.value)}
                inputProps={{ step: 0.01, min: 0, max: 1 }}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button size="small" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            color="warning"
            disabled={finishMutation.isLoading}
            onClick={handleSubmit}
          >
            {finishMutation.isLoading ? 'Finishing group…' : 'Finish group'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
